// @flow

import React, { type Node, useEffect } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { InputMask } from '@tectonics/components/input-mask';
import { useUserInputAsTextFieldWithFormatting } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';

import { TextField } from '../../../atoms';
import { useCurrentAllFeatures } from '@tectonics/prs/payment-spec';

import type { Feature } from '@tectonics/prs/feature';

type InputData = {|
  inputFieldName: string,
  label: string | (() => string) | (() => Node),
  specDocumentName: string,
|};

type FieldCustomerDocumentProps = {|
  inputData: InputData,
  mask: any,
  normalizer: (value: string) => string,
  placeholder?: string,
  type?: string,
  validationErrorRenderer: (validationError: any) => Node,
  additionalContent?: Node,
  mustShowHook?: (fieldName: string) => boolean,
  formatRaw?: (rawValue: string) => string,
  onValueChange?: (value: string) => void,
  required?: boolean,
|};

export function FieldCustomerDocument({
  inputData,
  mask,
  normalizer,
  placeholder,
  type = 'tel',
  validationErrorRenderer,
  additionalContent,
  mustShowHook,
  formatRaw,
  onValueChange,
  required = true,
}: FieldCustomerDocumentProps): Node {
  const { inputFieldName, label, specDocumentName } = inputData;
  const currentFeatures = useCurrentAllFeatures();
  const inputId = useUniqueId();
  const submitting = useSubmitting();

  const [document, handleChangeDocument] = useUserInputAsTextFieldWithFormatting(
    inputFieldName,
    formatRaw || ((value: string) => value),
    normalizer,
  );

  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError(inputFieldName);
  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('document');

  const mustShow = mustShowHook ? mustShowHook(inputFieldName) : true;

  useEffect(() => {
    if (onValueChange && document) {
      onValueChange(document);
    }
  }, [document, onValueChange]);

  if (isOptionalFieldByPaymentType || !mustShow) {
    return null;
  }

  const displayLabel = typeof label === 'function' ? label() : label;
  const isDocumentDisabled = getDocumentDisabled(currentFeatures, specDocumentName);
  const showError = formHasBeenSent && validationError && (required || validationError.id === 'server-error');

  return (
    <div data-test-name={'customer-document'}>
      <label htmlFor={inputId}>{displayLabel}</label>

      <TextField
        as={InputMask}
        id={inputId}
        type={type}
        value={document || ''}
        onChange={handleChangeDocument}
        placeholder={placeholder}
        fashion={showError ? 'error' : null}
        mask={mask}
        readOnly={submitting}
        data-error={showError ? 'error' : null}
        name={'customer-document'}
        disabled={isDocumentDisabled}
      />

      {showError && validationErrorRenderer({validationError})}
      {additionalContent}
    </div>
  );
}

function getDocumentDisabled(currentFeatures: ImmutableArray<Feature>, specDocumentFieldName: string): boolean {
  const documentFeature: ?Feature = currentFeatures.find((feature: Feature) => feature.name === specDocumentFieldName);
  const traits = documentFeature ? documentFeature.traits : null;
  if(!traits) {
    return false;
  }
  return traits.includes('disabled');
}
